import { useMemo } from 'react'

import { ColorVariant } from '@makyoapp/makyo-tw-ui'
import { SelectDropdown, Tip, useTip } from '@makyoapp/makyo-ui'

import { useAppBrandKitContext } from '@/context/AppBrandKitContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'

interface BrandKitColorsDropdownProps {
  id?: string
  value: string
  onChange: (value: ColorVariant) => void
  showExtendedColors?: boolean

  /** @deprecated */
  type?: 'bg' | 'fg' | 'both'
  /** @deprecated */
  schemeId?: string
}

/**
 * New BrandKit Color Dropdown
 * it also come with extended custom colors
 */
export function BrandKitColorsDropdown({ value, onChange, showExtendedColors = false }: BrandKitColorsDropdownProps) {
  const { brandkitExtendedColors } = useAppBrandKitContext()

  const [scheme] = useBuilderColorSchemeV2()
  const mode = scheme.current

  //
  const handleChange = (newValue: ColorVariant): void => {
    onChange(newValue)
  }

  function validateValue(value: string) {
    if (!value) return 'primary-40'

    if (typeof value === 'object' && 'light' in value && 'dark' in value) {
      return value[mode] ?? 'primary-40'
    }

    if (value.includes('--md-sys-color-')) {
      return 'primary-40'
    }
    if (value.includes('--color-')) {
      return value.replace('--color-', '')
    }
    return value
  }

  const groupedColors = useMemo(
    () => [
      {
        title: 'Primary',
        items: [
          // First the title
          { displayName: 'Primary', tokenName: 'primary-title', type: 'Title' },
          { displayName: 'Primary 0', tokenName: 'primary-0' },
          { displayName: 'Primary 5', tokenName: 'primary-5' },
          { displayName: 'Primary 10', tokenName: 'primary-10' },
          { displayName: 'Primary 20', tokenName: 'primary-20' },
          { displayName: 'Primary 30', tokenName: 'primary-30' },
          { displayName: 'Primary 40', tokenName: 'primary-40' },
          { displayName: 'Primary 50', tokenName: 'primary-50' },
          { displayName: 'Primary 60', tokenName: 'primary-60' },
          { displayName: 'Primary 70', tokenName: 'primary-70' },
          { displayName: 'Primary 80', tokenName: 'primary-80' },
          { displayName: 'Primary 90', tokenName: 'primary-90' },
          { displayName: 'Primary 95', tokenName: 'primary-95' },
          { displayName: 'Primary 100', tokenName: 'primary-100' },
        ],
      },
      {
        title: 'Secondary',
        items: [
          { displayName: 'Secondary', tokenName: 'secondary-title', type: 'Title' },
          { displayName: 'Secondary 0', tokenName: 'secondary-0' },
          { displayName: 'Secondary 5', tokenName: 'secondary-5' },
          { displayName: 'Secondary 10', tokenName: 'secondary-10' },
          { displayName: 'Secondary 20', tokenName: 'secondary-20' },
          { displayName: 'Secondary 30', tokenName: 'secondary-30' },
          { displayName: 'Secondary 40', tokenName: 'secondary-40' },
          { displayName: 'Secondary 50', tokenName: 'secondary-50' },
          { displayName: 'Secondary 60', tokenName: 'secondary-60' },
          { displayName: 'Secondary 70', tokenName: 'secondary-70' },
          { displayName: 'Secondary 80', tokenName: 'secondary-80' },
          { displayName: 'Secondary 90', tokenName: 'secondary-90' },
          { displayName: 'Secondary 95', tokenName: 'secondary-95' },
          { displayName: 'Secondary 100', tokenName: 'secondary-100' },
        ],
      },
      {
        title: 'Tertiary',
        items: [
          { displayName: 'Tertiary', tokenName: 'tertiary-title', type: 'Title' },
          { displayName: 'Tertiary 0', tokenName: 'tertiary-0' },
          { displayName: 'Tertiary 5', tokenName: 'tertiary-5' },
          { displayName: 'Tertiary 10', tokenName: 'tertiary-10' },
          { displayName: 'Tertiary 20', tokenName: 'tertiary-20' },
          { displayName: 'Tertiary 30', tokenName: 'tertiary-30' },
          { displayName: 'Tertiary 40', tokenName: 'tertiary-40' },
          { displayName: 'Tertiary 50', tokenName: 'tertiary-50' },
          { displayName: 'Tertiary 60', tokenName: 'tertiary-60' },
          { displayName: 'Tertiary 70', tokenName: 'tertiary-70' },
          { displayName: 'Tertiary 80', tokenName: 'tertiary-80' },
          { displayName: 'Tertiary 90', tokenName: 'tertiary-90' },
          { displayName: 'Tertiary 95', tokenName: 'tertiary-95' },
          { displayName: 'Tertiary 100', tokenName: 'tertiary-100' },
        ],
      },
      {
        title: 'Neutral ',
        items: [
          { displayName: 'Neutral', tokenName: 'neutral-title', type: 'Title' },
          { displayName: 'Neutral 0', tokenName: 'neutral-0' },
          { displayName: 'Neutral 5', tokenName: 'neutral-5' },
          { displayName: 'Neutral 10', tokenName: 'neutral-10' },
          { displayName: 'Neutral 20', tokenName: 'neutral-20' },
          { displayName: 'Neutral 30', tokenName: 'neutral-30' },
          { displayName: 'Neutral 40', tokenName: 'neutral-40' },
          { displayName: 'Neutral 50', tokenName: 'neutral-50' },
          { displayName: 'Neutral 60', tokenName: 'neutral-60' },
          { displayName: 'Neutral 70', tokenName: 'neutral-70' },
          { displayName: 'Neutral 80', tokenName: 'neutral-80' },
          { displayName: 'Neutral 90', tokenName: 'neutral-90' },
          { displayName: 'Neutral 95', tokenName: 'neutral-95' },
          { displayName: 'Neutral 100', tokenName: 'neutral-100' },
        ],
      },
      ...(showExtendedColors && brandkitExtendedColors.length > 0
        ? [
            {
              title: 'Extended',
              items: [
                { displayName: 'Extended', tokenName: 'extended-title', type: 'Title' },
                ...brandkitExtendedColors.map((c) => ({
                  displayName: c.label,
                  tokenName: c.label.toLocaleLowerCase().split(' ').join('-'),
                })),
              ],
            },
          ]
        : []),
    ],
    [brandkitExtendedColors, showExtendedColors]
  )

  const allColorOptions = [...groupedColors.flatMap((group) => group.items.map((item) => item))]

  const colorOnlyOptions = [...groupedColors.flatMap((group) => group.items.map((item) => item.tokenName))]

  return (
    <SelectDropdown
      value={validateValue(value)}
      onChange={(opt) => (opt.includes('-title') || opt.includes('-subtitle') ? null : handleChange(opt))}
      renderOption={(opt: any) => <RenderColorOption opt={opt} allColorOptions={allColorOptions} />}
      options={colorOnlyOptions}
      withSearch
      enableAutoScroll
    />
  )
}

const RenderColorOption = ({ opt, allColorOptions }: { opt: string; allColorOptions: any[] }) => {
  const { x, y, strategy, refs, context, placement, isOpen, arrowRef, getReferenceProps, getFloatingProps } = useTip()

  const tokenName = opt.includes('--color-') ? opt.replace('--color-', '') : opt

  const selectedColor = allColorOptions.find((item) => item.tokenName === tokenName)
  const backgroundColor = selectedColor?.tokenName ? `var(--color-${selectedColor.tokenName})` : undefined

  const renderColorOption = (selectedColor?: (typeof allColorOptions)[0]) => {
    if (!selectedColor) return null

    const styles: Record<string, string> = {
      Title: 'py-0.5 text-xs font-medium text-gray-400 dark:text-gray-200 uppercase cursor-default',
      SubTitle:
        'py-0.5 text-[0.65rem] leading-[1rem] font-medium text-gray-400 dark:text-gray-200 uppercase cursor-default',
      Divider: 'py-1 text-xs font-medium text-gray-500 dark:text-gray-200 cursor-default',
      Background: 'flex items-center gap-2',
    }

    if (selectedColor?.type === 'Title' || selectedColor?.type === 'Divider') {
      return <p className={styles[selectedColor.type]}>{selectedColor.displayName}</p>
    }

    if (selectedColor?.type === 'SubTitle') {
      return <p className={styles[selectedColor.type]}>{selectedColor.displayName}</p>
    }

    return (
      <>
        <div className={styles.Background}>
          <div className='w-4 h-4 rounded-full text-xs border bdr' style={{ backgroundColor }} />
          <p className='flex items-center gap-2'>
            {selectedColor?.displayName}
            {selectedColor?.displayName === 'Source' && (
              <span className='text-red-500' ref={refs.setReference} {...getReferenceProps()}>
                ⚠️
              </span>
            )}
          </p>
        </div>

        {selectedColor?.displayName === 'Source' && isOpen && (
          <Tip
            x={x}
            y={y}
            placement={placement}
            strategy={strategy}
            ref={refs.setFloating}
            {...getFloatingProps()}
            context={context as any}
            arrowRef={arrowRef}
          >
            <div className='w-[255px]'>
              This color token is not adaptive and may not be ideal for both dark and light themes.
            </div>
          </Tip>
        )}
      </>
    )
  }

  return <div className='flex items-center w-full gap-2'>{renderColorOption(selectedColor)}</div>
}
